class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.abortController = null;
    this.focusElement = this.input;
    this.resetButton.addEventListener("click", this.clear.bind(this));
    this.input.addEventListener(
      "input",
      FoxTheme.utils.debounce(this.onChange.bind(this), 300),
    );
    this.input.addEventListener("focus", this.onFocus.bind(this));
    document.addEventListener("pointerdown", this.handlePointerDown.bind(this));
    this.searchContent = this.querySelector(".search__content");
    this.searchRecommendationEmpty =
      this.dataset.searchRecommendationEmpty === "true";

    const isTemplateSearch = this.closest(".template-search");
    if (isTemplateSearch) {
      this.handleClickOutside = this.handleClickOutside.bind(this);
      document.addEventListener("pointerdown", this.handleClickOutside);
    }
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }
  get sectionId() {
    return FoxTheme.utils.getSectionId(this);
  }
  get resultsContainerId() {
    return "PredictiveSearchResults-" + this.sectionId;
  }

  onFocus() {
    if (this.closest(".template-search")) {
      document.body.classList.add("predictive-search-open");
      if (this.getQuery().length === 0) {
        return;
      }
      this.renderSection(this.setupPredictiveURL().toString());
    }
  }

  handlePointerDown(event) {
    const path = event.composedPath?.() || [];

    if (path.includes(this)) {
      return;
    }

    if (this.searchRecommendationEmpty && this.searchContent) {
      this.searchContent.classList.add("hidden");
    }

    this.input.value = "";
    this.removeAttribute("results");
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event && event.preventDefault();
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    if (this.searchRecommendationEmpty && this.searchContent) {
      this.searchContent.classList.add("hidden");
    }
    this.input.value = "";
    this.input.focus();
    this.removeAttribute("results");
  }

  setupPredictiveURL() {
    const url = new URL(
      `${window.shopUrl}${FoxTheme.routes.predictive_search_url}`,
    );
    url.searchParams.set("q", this.getQuery());
    url.searchParams.set("resources[type]", "product");
    url.searchParams.set("resources[limit]", "10");
    url.searchParams.set("resources[limit_scope]", "each");
    url.searchParams.set("section_id", this.sectionId);
    return url;
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    this.renderSection(this.setupPredictiveURL().toString());
  }

  renderSection(url) {
    const query = this.getQuery();
    if (this.cachedMap.has(url)) {
      this.renderSectionFromCache(url, query);
      return;
    }
    this.renderSectionFromFetch(url, query);
  }

  renderSectionFromCache(url, query) {
    if (this.getQuery() !== query) {
      return;
    }
    this.renderSearchResults(this.cachedMap.get(url));
    this.setAttribute("results", "");
  }

  fetchSection(url, signal) {
    return fetch(url, { signal }).then((response) => {
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return response.text();
    });
  }

  resolveSectionResponse(searchUrl, signal) {
    if (this.cachedMap.has(searchUrl)) {
      return Promise.resolve(this.cachedMap.get(searchUrl));
    }

    return this.fetchSection(searchUrl, signal).then((searchText) => {
      this.cachedMap.set(searchUrl, searchText);
      return searchText;
    });
  }

  renderSectionFromFetch(url, query) {
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.setAttribute("loading", "true");
    this.resetButton.classList.add("btn--loading");

    this.resolveSectionResponse(url, signal)
      .then((responseText) => {
        if (this.getQuery() !== query) {
          return;
        }
        this.renderSearchResults(responseText);
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          return;
        }
        console.error("Error fetching data: ", error);
        this.setAttribute("error", "Failed to load data");
      })
      .finally(() => {
        if (this.getQuery() !== query) {
          return;
        }
        this.removeAttribute("loading");
        this.resetButton.classList.remove("btn--loading");
        this.setAttribute("results", "true");
      });
  }

  renderSearchResults(responseText) {
    const targetElement = document.getElementById(this.resultsContainerId);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, "text/html");
      const contentElement = parsedDoc.getElementById(this.resultsContainerId);

      if (contentElement) {
        this.searchContent?.classList.remove("hidden");
        targetElement.innerHTML = contentElement.innerHTML;
      } else {
        console.error(
          `Element with id '${this.resultsContainerId}' not found in the parsed response.`,
        );
      }
    } else {
      console.error(
        `Element with id '${this.resultsContainerId}' not found in the document.`,
      );
    }
  }

  handleClickOutside(event) {
    const path = event.composedPath?.() || [];

    if (!path.includes(this)) {
      requestAnimationFrame(() => {
        document.body.classList.remove("predictive-search-open");
      });
    }
  }
}
customElements.define("predictive-search", PredictiveSearch, {
  extends: "form",
});

class SearchDrawer extends DrawerComponent {
  constructor() {
    super();
  }
  get requiresBodyAppended() {
    return false;
  }
  get input() {
    return this.querySelector('input[type="search"]');
  }
  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
}
customElements.define("search-drawer", SearchDrawer);

class SearchResultsLoader {
  constructor() {
    this.container = document.getElementById("ProductGridContainer");
    if (!this.container?.hasAttribute("data-search-products")) {
      return;
    }

    this.query = this.container.dataset.query;
    this.sectionId = this.container.dataset.sectionId;
    if (!this.query || !this.sectionId) {
      return;
    }

    this.loadResults();
  }

  loadResults() {
    const url = new URL(
      `${window.shopUrl}${FoxTheme.routes.predictive_search_url}`,
    );
    url.searchParams.set("q", this.query);
    url.searchParams.set("resources[type]", "product");
    url.searchParams.set("resources[limit]", "10");
    url.searchParams.set("section_id", this.sectionId);

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const results = doc.getElementById(
          `SearchFallbackResults-${this.sectionId}`,
        );
        if (!results) {
          return;
        }

        this.container.innerHTML = results.innerHTML;
        this.container.removeAttribute("data-search-products");

        const templateSearch = this.container.closest(".template-search");
        const productGrid = this.container.querySelector("#ProductsList");
        const resultCount = productGrid
          ? parseInt(productGrid.dataset.resultCount || "0", 10)
          : 0;

        if (resultCount > 0) {
          templateSearch?.classList.remove("template-search--empty");
          this.updateProductCount(resultCount);
        } else {
          templateSearch?.classList.add("template-search--empty");
        }
      })
      .catch((error) => {
        console.error("Search results failed to load: ", error);
      });
  }

  updateProductCount(count) {
    const productCount = document.getElementById("ProductCount");
    if (!productCount) {
      return;
    }

    const template = productCount.textContent || "";
    const updated = template.replace(/\d+/, String(count));
    if (updated !== template) {
      productCount.textContent = updated;
    }
  }
}

document.addEventListener("DOMContentLoaded", () => {
  new SearchResultsLoader();
});

